import { generateDynamicPath } from 'common/utils/routing';

/**
 * Build a Natalie route path while preserving the current URL query — notably
 * `?testInviteCode=`, the unified Natalie test signal.
 *
 * `generateDynamicPath`'s `queryParams` arg builds a *fresh* query from the
 * passed object only; it does not read or merge `location.search`. This helper
 * wraps it so every Natalie navigation carries the existing query forward
 * (caller-provided params merged on top, overriding any same-named key), so a
 * navigation cannot silently drop the test signal. Pure/string-level — no
 * router coupling — so it is trivially testable and usable from both
 * `history.push` call sites and redux `push` effects.
 *
 * @param route        a Natalie route template (e.g. `NatalieRoutes.AccountlessJoin`)
 * @param variables    path params to interpolate into the template
 * @param currentSearch the current `location.search` (with or without a leading `?`)
 * @param extraParams  query params to merge on top of the preserved query
 * @param options      `{ isAbsolute: true }` to prefix the origin (shareable links)
 */
export const buildNataliePath = (
  route: string,
  variables: Record<string, unknown> = {},
  currentSearch = '',
  extraParams: Record<string, string> = {},
  options?: { isAbsolute?: boolean }
): string => {
  const path = generateDynamicPath(route, variables, undefined, options?.isAbsolute ? { isAbsolute: true } : undefined);

  const params = new URLSearchParams(currentSearch);
  Object.entries(extraParams).forEach(([key, value]) => params.set(key, value));

  const query = params.toString();
  return query ? `${path}?${query}` : path;
};
